import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardStyle } from '@capacitor/keyboard';
import { Style, StatusBar } from '@capacitor/status-bar';
import type { ServerTheme } from '@sirocco/shared';
import { themeFor, type Ambiance } from './ambiance';
import { POLICES_DE_VILLE } from './polices';

/**
 * Theme de repli, utilise avant qu'un spot soit choisi.
 * Ce sont les couleurs de Marrakech, seul serveur ouvert.
 */
export const DEFAULT_THEME: ServerTheme = {
  illustration: 'marrakech',
  primary: '#B8451B',
  primaryDark: '#9A3714',
  primarySoft: '#F6E4D8',
  background: '#FBF4EC',
  surface: '#FFF8F0',
  surfaceMuted: '#F3E6D4',
  text: '#43291C',
  textMuted: '#806250',
  border: '#F0E0CD',
};

/**
 * Repeint l'application aux couleurs d'un spot, a l'heure choisie.
 *
 * Aucun ecran ne connait la couleur de Marrakech : ils lisent tous
 * `var(--s-primary)`. Ouvrir une nouvelle ville se resume donc a ajouter
 * son theme dans le catalogue cote serveur — et sa nuit s'en deduit.
 *
 * L'attribut `data-ambiance` porte le mode sur la racine : ce que les
 * variables ne savent pas exprimer — les couleurs de sens, l'habillage
 * natif des champs — s'y accroche dans la feuille de style.
 */
export function applyServerTheme(theme: ServerTheme, ambiance: Ambiance = 'day'): void {
  const root = document.documentElement;
  const applique = themeFor(theme, ambiance);

  root.dataset.ambiance = ambiance;
  root.style.setProperty('--s-primary', applique.primary);
  root.style.setProperty('--s-primary-dark', applique.primaryDark);
  root.style.setProperty('--s-primary-soft', applique.primarySoft);
  root.style.setProperty('--s-bg', applique.background);
  root.style.setProperty('--s-surface', applique.surface);
  root.style.setProperty('--s-surface-muted', applique.surfaceMuted);
  root.style.setProperty('--s-text', applique.text);
  root.style.setProperty('--s-text-muted', applique.textMuted);
  root.style.setProperty('--s-border', applique.border);

  /*
    La lettre de la ville, comme sa couleur : posee ici, lue par qui en a
    besoin.

    Une ville sans police propre **retire** les jetons au lieu de les
    vider. Un jeton vide reste un jeton defini : la valeur de secours du
    `var()` ne joue pas, et le titre tombait alors sur la police du
    systeme en graisse normale au lieu de celle de l'application. Les
    retirer est aussi ce qui evite qu'une ville herite de la lettre de la
    precedente.
  */
  const police = POLICES_DE_VILLE[theme.illustration];
  for (const [jeton, valeur] of [
    ['--s-title-font', police?.famille],
    ['--s-title-weight', police && String(police.graisse)],
    ['--s-title-tracking', police?.interlettre],
  ] as const) {
    if (valeur) root.style.setProperty(jeton, valeur);
    else root.style.removeProperty(jeton);
  }

  /*
    Les ombres reprennent la teinte du texte du spot, pour rester chaudes
    a Marrakech et froides a New York. De nuit, ce texte est presque
    blanc : on retombe alors sur du noir, une ombre claire ne creusant
    rien sur un fond sombre.
  */
  const shadowRgb = ambiance === 'night' ? '0, 0, 0' : hexToRgb(applique.text);
  const force = ambiance === 'night' ? 2.4 : 1;
  root.style.setProperty('--shadow-card', `0 1px 3px rgba(${shadowRgb}, ${0.05 * force})`);
  root.style.setProperty('--shadow-raised', `0 6px 18px rgba(${shadowRgb}, ${0.1 * force})`);
  root.style.setProperty('--shadow-float', `0 10px 26px rgba(${shadowRgb}, ${0.18 * force})`);

  // La barre d'etat du telephone s'accorde a l'illustration.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', applique.background);

  applyNativeStatusBar(ambiance);
  applyNativeKeyboard(ambiance);
}

/**
 * La barre d'etat de l'application native.
 *
 * Dans l'enveloppe Capacitor, la vue web occupe tout l'ecran, encoche
 * comprise : l'heure et la batterie se posent donc sur notre fond. iOS
 * les dessine en sombre par defaut, ce qui devient illisible sur le bleu
 * de nuit — d'ou ce basculement.
 *
 * Sur le web, il n'y a pas de barre d'etat a piloter : la balise
 * `theme-color` ci-dessus fait deja le travail, et on ne touche a rien.
 */
function applyNativeStatusBar(ambiance: Ambiance): void {
  if (!Capacitor.isNativePlatform()) return;
  void StatusBar.setStyle({ style: ambiance === 'night' ? Style.Dark : Style.Light }).catch(() => {
    // Barre absente ou plugin indisponible : l'application reste lisible.
  });
}

/**
 * Le clavier du telephone.
 *
 * Il se colle au composeur d'une discussion, sans plus rien entre eux : sa
 * teinte doit donc suivre celle de l'application, et non celle du systeme.
 * Sans cela, un membre en mode jour sur un telephone en mode sombre voit un
 * clavier noir sous un fond sable.
 */
function applyNativeKeyboard(ambiance: Ambiance): void {
  if (!Capacitor.isNativePlatform()) return;
  void Keyboard.setStyle({
    style: ambiance === 'night' ? KeyboardStyle.Dark : KeyboardStyle.Light,
  }).catch(() => {
    // Plugin indisponible : le clavier garde l'habillage du systeme.
  });
}

/**
 * Repasse aux couleurs de la marque : ecrans de connexion, inscription
 * et choix du spot, qui restent cyan quel que soit le serveur.
 */
export function applyGlobalTheme(): void {
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#EDF7FA');
}

/** "#C1441E" -> "193, 68, 30", pour composer des rgba(). */
function hexToRgb(hex: string): string {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value;
  const int = Number.parseInt(full, 16);
  return `${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}`;
}
